import { useCallback, useEffect, useRef, useState } from "react";
import { useBackState } from "@/hooks/useScrollMemory";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MatchBadge } from "@/components/MatchBadge";
import { ChevronDown, ChevronRight, Info, Truck, Wrench, ShieldCheck, Eye, HelpCircle, AlertTriangle, CircleSlash, MessageCircleQuestion } from "lucide-react";

/** "We don't have one" is NOT "we haven't recorded it yet."
 *  ONE implementation, rendered identically on the Vehicles page, the Search Trucks drawer and
 *  the on-donor band — the server decides whether there's an honest number to show. */
export function CaptureGapNote({ gap, testid = "capture-gap" }) {
  if (!gap) return null;
  // Ambiguous = "we couldn't tell which spec you meant", said out loud so the ABSENCE of a
  // number never reads as "there is no gap". A line naming the wrong field would be worse.
  if (gap.state === "ambiguous") {
    if (!gap.note) return null;
    return (
      <div data-testid={`${testid}-ambiguous`}
        className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>{gap.note}</span>
      </div>
    );
  }
  if (!gap.message) return null;
  return (
    <div data-testid={testid}
      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-[#8a5a12] flex items-start gap-2">
      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>{gap.message}
        {/* this line carries two counts, so it states its own seeded share rather than leaving
            them to be read as real (owner ruling 2026-09-23) */}
        {gap.synthetic_note && (
          <b data-testid={`${testid}-synth`} className="block mt-0.5 text-[11px] text-[#B01E28]">{gap.synthetic_note}</b>
        )}
      </span>
    </div>
  );
}

// A shelf with five tagged ones already answers the question, and opening the band would push
// that answer down the page. Empty or thin, the truck is the answer (owner ruling 2026-06-01).
const THIN_SHELF = 3;

const TONE = {
  good: { cls: "bg-emerald-600 text-white border-transparent", Icon: ShieldCheck },
  plain: { cls: "bg-slate-700 text-white border-transparent", Icon: Eye },
  unknown: { cls: "bg-white text-slate-500 border border-dashed border-slate-400", Icon: HelpCircle },
  looked: { cls: "bg-amber-100 text-amber-800 border border-amber-300", Icon: AlertTriangle },
  bad: { cls: "bg-[#B01E28] text-white border-transparent", Icon: AlertTriangle },
  gone: { cls: "bg-zinc-200 text-zinc-700 border border-zinc-300", Icon: CircleSlash },
};

/** IS IT THERE, AND IS IT ANY GOOD — on the row, with who says so. A result row without this is
 *  a GONE engine that looks exactly like a GOOD one. */
function CondBadge({ cond, stock }) {
  if (!cond) return null;
  const t = TONE[cond.tone] || TONE.unknown;
  const { Icon } = t;
  const who = cond.provenance?.line || (cond.at ? `${cond.by || "recorded"} · ${String(cond.at).slice(0, 10)}` : null);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span data-testid={`on-donor-cond-${stock}`}
        className={`rounded-md px-1.5 py-0.5 font-mono font-bold uppercase tracking-wide text-[9px] ${t.cls}`}>
        <Icon className="inline w-3 h-3 mr-1 -mt-0.5" />{cond.state_short}
        {cond.reason_label ? ` · ${cond.reason_label}` : ""}
      </span>
      <span data-testid={`on-donor-cond-note-${stock}`} className="text-[11px] text-slate-500">
        {cond.derived_why || cond.note_line}{who ? <span className="text-slate-400"> · {who}</span> : null}
        {/* HOW OLD IS THAT RATING? A bare date can be read straight past. An engine rated GOOD
            eighteen months ago on a truck that has sat in the weather isn't the same promise. */}
        {cond.provenance?.age_line ? (
          <span data-testid={`on-donor-cond-age-${stock}`}
            className={cond.provenance.stale ? "text-[#C76714] font-semibold" : "text-slate-400"}>
            {" "}· {cond.provenance.age_line}
          </span>
        ) : null}
      </span>
    </span>
  );
}

/** ASK THE YARD TO GO AND LOOK — the moment that makes assessment happen. A salesperson searching
 *  for a part is the natural trigger: the engines that get looked at become the ones customers are
 *  actually asking for. Offered ONLY where we genuinely don't know (nobody has looked, or somebody
 *  looked and couldn't tell) — never on something already rated, or the queue fills with work
 *  nobody needs to do. */
function AskAction({ t, q, onAsked }) {
  const cond = t.condition;
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [asked, setAsked] = useState(null);
  const ask = asked || cond?.ask;
  if (!cond?.askable) return null;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/requests/assess/ask", {
        donor_id: t.id, component: cond.component, note, from_query: q || "",
      });
      setAsked(data.ask);
      setOpen(false); setNote("");
      toast.success("The yard has been told — you'll get the answer when it's rated");
      onAsked?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (open) {
    return (
      <div data-testid={`on-donor-ask-panel-${t.stock_number}`}
        className="w-full rounded-lg border-2 border-[#E0781A]/60 bg-[#FFF7ED] p-2 space-y-1.5">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#8a4b0b]">
          Ask the yard to go and check this {String(cond.component_label || "component").toLowerCase()}
        </p>
        <div className="flex items-center gap-1.5">
          <Input data-testid={`on-donor-ask-note-${t.stock_number}`} value={note} autoFocus
            onChange={(e) => setNote(e.target.value)}
            placeholder="For what? e.g. customer needs it by Friday (optional)"
            className="h-8 text-xs rounded-lg border-[#E0781A]/50 bg-white" />
          <button type="button" data-testid={`on-donor-ask-send-${t.stock_number}`} disabled={busy}
            onClick={send}
            className="h-8 px-3 rounded-lg bg-[#B01E28] text-white text-xs font-bold disabled:opacity-40">
            Ask
          </button>
        </div>
        <button type="button" data-testid={`on-donor-ask-cancel-${t.stock_number}`}
          onClick={() => setOpen(false)} className="text-[11px] text-slate-500 underline">cancel</button>
      </div>
    );
  }
  if (ask) {
    return (
      <span data-testid={`on-donor-asked-${t.stock_number}`}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[#E0781A]/60 bg-[#FFF7ED] px-3 h-9 text-xs font-semibold text-[#8a4b0b]">
        <MessageCircleQuestion className="w-3.5 h-3.5" />
        Asked{ask.count > 1 ? ` · ${ask.count}` : ""}
        <button type="button" data-testid={`on-donor-ask-again-${t.stock_number}`}
          onClick={() => setOpen(true)} className="underline font-normal">ask again</button>
      </span>
    );
  }
  return (
    <button type="button" data-testid={`on-donor-ask-${t.stock_number}`} onClick={() => setOpen(true)}
      className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[#E0781A]/60 bg-white px-3 h-9 text-xs font-semibold text-[#8a4b0b] hover:bg-[#FFF7ED] transition-colors">
      <MessageCircleQuestion className="w-3.5 h-3.5" />
      Ask the yard to check it
    </button>
  );
}

function OnDonorRow({ t, prefix = "on-donor-row", q, onAsked }) {
  const m = t.match || {};
  const comp = t.condition?.component_label || m.component_label;
  const more = m.terms_total > m.terms_covered ? m.terms_total - m.terms_covered : 0;
  // A GONE component is NOT "not yet pulled" and there is nothing to go and pull — saying either
  // on this row would be the same lie the band exists to remove.
  const gone = t.condition?.state === "gone";
  return (
    <div data-testid={`${prefix}-${t.stock_number}`} className="p-3 flex flex-wrap items-start gap-x-3 gap-y-1.5">
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <Wrench className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-sm font-semibold">
            {comp ? `${comp}` : "Spec match"}
            {m.number_matched ? <span className="text-slate-500 font-normal"> — {m.number_matched}</span> : null}
          </span>
          <MatchBadge match={m} />
        </div>
        <div className="mt-1"><CondBadge cond={t.condition} stock={t.stock_number} /></div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
          <span data-testid={`on-donor-badge-${t.stock_number}`}
            className={`rounded-md px-1.5 py-0.5 font-mono font-bold uppercase tracking-wide text-[9px] ${gone
              ? "bg-zinc-200 text-zinc-700 border border-zinc-300"
              : "bg-[#B01E28] text-white"}`}>
            {gone ? `RECORDED ON ${t.stock_number} — NOT ON IT NOW`
              : `ON DONOR ${t.stock_number} — NOT YET PULLED`}
          </span>
          <span className="text-slate-500">{[t.year, t.make, t.model].filter(Boolean).join(" ")}</span>
          {m.matched_in && (
            <span data-testid={`on-donor-why-${t.stock_number}`} className="text-slate-400 font-mono">
              matched: {m.matched_in}{more ? ` +${more} more field${more === 1 ? "" : "s"}` : ""}
            </span>
          )}
        </div>
      </div>
      <AskAction t={t} q={q} onAsked={onAsked} />
      <Link data-testid={`on-donor-go-${t.stock_number}`} to={`/donors/${t.id}`}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 h-9 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
        {gone ? "Open the truck record" : "Go pull this — needs a tag"}
      </Link>
    </div>
  );
}

/** Rows with a subtle line wherever the match QUALITY changes, so a partial match is never read
 *  as the thing that was asked for. */
function TieredRows({ items, q, onAsked }) {
  let last = null;
  return items.map((t) => {
    const tier = t.match_tier;
    const show = tier && tier.code !== last;
    last = tier?.code || last;
    return (
      <div key={t.id}>
        {show && (
          <div data-testid={`on-donor-tier-${tier.code}`}
            className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-widest text-slate-400 bg-slate-50/70 border-t border-slate-100">
            {tier.label}
          </div>
        )}
        <OnDonorRow t={t} q={q} onAsked={onAsked} />
      </div>
    );
  });
}

function Pill({ on, children, ...rest }) {
  return (
    <button type="button" {...rest}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 h-7 text-[11px] font-semibold transition-colors ${on
        ? "border-slate-800 bg-slate-800 text-white"
        : "border-slate-300 bg-white text-slate-500 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}

/** ON-DONOR band for the parts page: the same /truck-search engine (NG-5 — one engine, a second
 *  window onto it), surfacing components that are still bolted to a truck, ORDERED BY WHAT CAN
 *  ACTUALLY BE SOLD — relevance group first, then condition inside it.
 *
 *  These rows are NOT parts: there is no part id, so there is nothing a cart could reference —
 *  the protection is structural, not a warning. Spawning needs a physical FAS tag at the truck,
 *  so the action is "go pull it", never "add to cart". Real shelf parts stay above this band. */
export function OnDonorBand({ q, shelfCount = null }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useBackState("odb.open", false);
  const [err, setErr] = useState(false);
  // A core is a different sale to a different customer, so it is visible by default and one tap
  // hides it. GONE is hidden by default and one tap away — it must never sit among sellables.
  const [showCores, setShowCores] = useBackState("odb.cores", true);
  const [showGone, setShowGone] = useBackState("odb.gone", false);
  const autoFor = useRef(null);

  const load = useCallback(async () => {
    if (!q?.trim()) { setData(null); return; }
    try {
      setErr(false);
      const { data } = await api.get("/truck-search", { params: { q, page_size: 25, bands: 1, facets: 0 } });
      setData(data);
    } catch { setData(null); setErr(true); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  // OPENS ITSELF WHEN THE SHELF CAN'T ANSWER (owner ruling 2026-06-01): empty shelf and there are
  // truck hits, or a thin shelf and at least one of those hits is something sellable. A stocked
  // shelf leaves it collapsed with its one-line summary, whatever shape the truck hits are in.
  useEffect(() => {
    const b = data?.bands;
    if (!b || shelfCount == null || autoFor.current === q) return;
    autoFor.current = q;
    if (!b.on_trucks) return;
    if (shelfCount === 0) setOpen(true);
    else if (shelfCount <= THIN_SHELF) setOpen(!!b.best_sellable_state);
  }, [data, q, shelfCount]);

  if (!q?.trim()) return null;
  if (err) {
    return (
      <div data-testid="on-donor-band-error" className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-2 text-[12px] text-slate-500">
        Couldn’t check what’s still on trucks — this band is incomplete, so don’t read it as “none”.
      </div>
    );
  }
  if (!data) return null;
  const b = data.bands || {};
  const sellable = data.items || [];
  const cores = b.cores?.items || [];
  const gone = b.gone?.items || [];
  const onTrucks = b.on_trucks || 0;
  const goneCount = b.gone?.count || 0;

  if (!onTrucks && !goneCount) {
    return (
      <div data-testid="on-donor-band" className="mt-4">
        <div data-testid="on-donor-none" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-[12px] text-slate-500 flex items-center gap-2">
          <Truck className="w-3.5 h-3.5 text-slate-400" />
          Nothing matching is recorded on a truck either.
        </div>
        <div className="mt-2"><CaptureGapNote gap={data.capture_gap} testid="on-donor-capture-gap" /></div>
      </div>
    );
  }
  const onlySellable = !showCores && !showGone;
  return (
    <div data-testid="on-donor-band" className="mt-4">
      <button type="button" data-testid="on-donor-toggle" onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 rounded-xl border border-[#E0781A]/40 bg-[#FFF7ED] px-3 py-2.5 text-left hover:bg-[#FFEEDB] transition-colors">
        {open ? <ChevronDown className="w-4 h-4 text-[#C76714] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#C76714] shrink-0" />}
        <Truck className="w-4 h-4 text-[#C76714] shrink-0" />
        <span className="text-sm text-[#8a4b0b]">
          {/* NEVER A BARE COUNT: the split arrives with the number, from the server, so the band
              and the truck page can never word the same fact differently. THE SERVER OWNS THE
              WORDING — `bands.line` already opens with the same number, so the leading figure is
              stripped here and re-emitted as its own element. Change one and change both. */}
          {onTrucks > 0 && (
            <><b data-testid="on-donor-count" className="font-mono">{onTrucks}</b>{" "}</>
          )}
          {data.synthetic?.in_results ? (
            <span data-testid="on-donor-synthetic" className="font-mono font-bold text-[#B01E28]">
              ({data.synthetic.in_results} SYNTHETIC){" "}
            </span>
          ) : null}
          <span data-testid="on-donor-line">
            {onTrucks > 0 ? (b.line || "").replace(/^\d+\s/, "") : (b.line || "")}
          </span>
          <span className="font-semibold"> {open ? "Hide" : "Show"}</span>
        </span>
      </button>

      {open && (
        <div data-testid="on-donor-rows" className="border-x border-b border-[#E0781A]/40 rounded-b-xl bg-white">
          <div className="p-2 border-b border-slate-100 flex flex-wrap items-center gap-2">
            <Pill data-testid="on-donor-only-sellable" on={onlySellable}
              title="Drop the cores and anything recorded gone — just what can be sold as a working part"
              onClick={() => { setShowCores(false); setShowGone(false); }}>
              Only what I can sell
            </Pill>
            <Pill data-testid="on-donor-include-cores" on={showCores}
              title="Known-bad components still sell as cores"
              onClick={() => setShowCores(!showCores)}>
              Include cores{b.cores?.count ? ` · ${b.cores.count}` : ""}
            </Pill>
            <Pill data-testid="on-donor-show-gone" on={showGone}
              title="Recorded gone — the truck had one and it isn't on it now"
              onClick={() => setShowGone(!showGone)}>
              Show gone too{goneCount ? ` · ${goneCount}` : ""}
            </Pill>
            {b.window_bounded && (
              <span data-testid="on-donor-window-bounded" className="text-[10px] font-mono text-slate-400">
                split counted over the first {data.relevance_window?.ranked} ranked matches
              </span>
            )}
          </div>
          <div className="p-2"><CaptureGapNote gap={data.capture_gap} testid="on-donor-capture-gap" /></div>

          <div className="divide-y divide-slate-100">
            {sellable.length ? <TieredRows items={sellable} q={q} onAsked={load} /> : (
              <div data-testid="on-donor-no-sellable" className="px-3 py-2 text-[12px] text-slate-500">
                Nothing here can be sold as a working part.
              </div>
            )}
          </div>
          {b.sellable?.count > sellable.length && (
            <div data-testid="on-donor-more" className="px-3 py-2 text-[11px] text-slate-500 border-t border-slate-100">
              showing {sellable.length} of {b.sellable.count} — open Search Trucks to see them all
            </div>
          )}

          {/* CORES ARE THEIR OWN GROUP — a different sale, to a different customer, for a
              different reason. Each one names the reason, which is what a core buyer asks. */}
          {showCores && cores.length > 0 && (
            <div data-testid="on-donor-cores" className="border-t-2 border-[#B01E28]/30">
              <div className="px-3 py-1.5 bg-red-50/60 text-[10px] font-mono uppercase tracking-widest text-[#8B1820]">
                Cores — sells as a core, not as a working part
              </div>
              <div className="divide-y divide-slate-100">
                {cores.map((t) => <OnDonorRow key={t.id} t={t} prefix="on-donor-core-row" q={q} onAsked={load} />)}
              </div>
            </div>
          )}

          {/* GONE is hidden by default and never sorts among the sellables. The link says GONE in
              plain words, and each row still names who recorded it gone and when, so a confirmed
              absence reads differently from an old guess. */}
          {goneCount > 0 && (
            <div className="border-t border-slate-200">
              {!showGone ? (
                <button type="button" data-testid="on-donor-gone-link" onClick={() => setShowGone(true)}
                  className="w-full text-left px-3 py-2 text-[11px] font-semibold text-slate-500 hover:bg-slate-50 transition-colors">
                  {goneCount} more recorded as gone — show
                </button>
              ) : (
                <div data-testid="on-donor-gone-rows">
                  <div className="px-3 py-1.5 bg-zinc-100 text-[10px] font-mono uppercase tracking-widest text-zinc-600 flex items-center justify-between">
                    <span>Recorded gone — truck had one, not present</span>
                    <button type="button" data-testid="on-donor-gone-hide" onClick={() => setShowGone(false)}
                      className="font-semibold text-zinc-500 hover:text-zinc-800">hide</button>
                  </div>
                  <div className="divide-y divide-slate-100 opacity-80">
                    {gone.map((t) => <OnDonorRow key={t.id} t={t} prefix="on-donor-gone-row" q={q} onAsked={load} />)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
